import { Icon, useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashIsEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import CalendarIcon from '../../Assets/calendar.svg';
import Button from '../../Components/Button';
import ListItem from '../../Components/ListItem';
import ResponsiveText from '../../Components/ResponsiveText';
import constants from '../../Config/constants';
import { pluralize } from '../../Helper';
import dateHelper from '../../Helper/Date';
import storeHelper from '../../Helper/Store';
import { OrderDetails } from '../../RTK/api/orders/types';
import themeStyle from '../../Theme/styles';

import Loader from './ReservationCardLoader';

type Props = OrderDetails & {
  loading?: boolean;
  onCardPress: (data: OrderDetails, isQR?: boolean) => void;
};

const ReservationCard = (props: Props) => {
  const theme = useTheme();
  const { loading, onCardPress, ...data } = props;

  const isReservation = lodashIsEmpty(data.event_details);
  const subTitle = useMemo(() => {
    if (loading) return '';
    const addOnNames = data.items.map((addOn) => addOn.name);
    if (isReservation) {
      return addOnNames.join(' - ');
    } else {
      return [data.event_details.name, ...addOnNames].join(' - ');
    }
  }, [isReservation, loading, data]);

  if (loading) return <Loader />;
  const { dining_length, event_details, items, order_time, store_details } =
    data;

  const reservationTime = dateHelper.getDayjsAtTime(order_time);

  return (
    <Button onPress={() => onCardPress(data)} plain>
      <View style={[styles.wrapper, { borderColor: theme['color-info-500'] }]}>
        <View style={themeStyle.flex1}>
          <ResponsiveText
            category="h6"
            numberOfLines={1}
            style={themeStyle.bold}
          >
            {store_details?.name}
          </ResponsiveText>
          <ResponsiveText
            category="s1"
            numberOfLines={1}
            style={themeStyle.spacingBottomXSmall}
          >
            {subTitle}
          </ResponsiveText>
          <ListItem
            contentContainerStyle={themeStyle.spacingBottomXSmall}
            leftIcon={{
              svg: CalendarIcon,
              size: 15,
            }}
            title={dayjs(data?.order_date).format(
              constants.DATE_DISPLAY_FORMAT2,
            )}
          />
          <ListItem
            contentContainerStyle={themeStyle.spacingBottomXSmall}
            leftIcon={{
              name: 'clock',
              size: 15,
            }}
            title={
              !isReservation
                ? dayjs(event_details?.start_date).format(
                    constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
                  )
                : `${[
                    reservationTime.format(
                      constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
                    ),
                    reservationTime
                      .add(dining_length, 'minute')
                      .format(constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT),
                  ].join(' - ')} ( ${storeHelper.getDiningDurationInHours(
                    dining_length,
                  )} )`
            }
          />
          <ListItem
            leftIcon={{
              name: 'people',
              size: 15,
            }}
            title={`${items?.[0]?.quantity} ${pluralize(
              items?.[0]?.quantity,
              'guest',
            )}`}
          />
        </View>
        <Button
          style={[
            themeStyle.alignItemsCenter,
            themeStyle.justifyContentCenter,
            themeStyle.spacingLeftMedium,
          ]}
          onPress={() => onCardPress(data, true)}
          plain
        >
          <Icon
            name="qrcode"
            pack="fa"
            fill={theme['text-basic-color']}
            style={{ width: 65, height: 65 }}
          />
          <ResponsiveText category="p2">View QR code</ResponsiveText>
        </Button>
      </View>
    </Button>
  );
};

export const styles = StyleSheet.create({
  wrapper: {
    ...themeStyle.pageVerticalSpacingMedium,
    ...themeStyle.pageHorizontalSpacing,
    ...themeStyle.flexDirectionRow,
    borderWidth: 1,
    borderRadius: 4,
  },
  editBtn: { borderRadius: 3 },
  editIcon: { marginHorizontal: 0 },
});

export default ReservationCard;
